import React from 'react';
import env from '../../env';

/**
 * AboutPageSchema Component
 * 
 * Implements Schema.org AboutPage structured data for About pages.
 * Wraps the existing Organization schema as mainEntity.
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/AboutPage
 * 
 * @param {Object} props
 * @param {string} props.name - Page title
 * @param {string} props.description - Page description
 * @param {string} props.url - Page URL (relative or absolute)
 * @param {Object} props.breadcrumb - Breadcrumb data (optional)
 * @param {string} props.image - Page image URL (optional)
 * 
 * @example
 * <AboutPageSchema
 *   name="About Us | Scunci"
 *   description="Learn about Scunci's history and commitment"
 *   url="/about"
 *   breadcrumb={breadcrumbData}
 * />
 */
export function AboutPageSchema({ 
  name, 
  description, 
  url, 
  breadcrumb,
  image 
}) {
  React.useEffect(() => {
    // Validate required data
    if (!name || !url) {
      return;
    }

    // Remove any existing about page schema script
    const existingScript = document.querySelector('script[data-about-page-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Build absolute URL
    let absoluteUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      const normalizedUrl = url.startsWith('/') ? url : '/' + url;
      absoluteUrl = env.baseOrigin + normalizedUrl;
    }

    // Build absolute image URL if provided
    let absoluteImageUrl = null;
    if (image) {
      if (image.startsWith('http://') || image.startsWith('https://')) {
        absoluteImageUrl = image;
      } else {
        const normalizedImage = image.startsWith('/') ? image : '/' + image;
        absoluteImageUrl = env.baseOrigin + normalizedImage;
      }
    }

    // Build AboutPage schema according to Schema.org specification
    const aboutPageSchema = {
      "@context": "https://schema.org/",
      "@type": "AboutPage",
      "@id": absoluteUrl,
      "name": name,
      "url": absoluteUrl
    };

    // Add description if provided
    if (description) {
      aboutPageSchema.description = description;
    }

    // Add image if provided
    if (absoluteImageUrl) {
      aboutPageSchema.image = absoluteImageUrl;
    }

    // Add breadcrumb reference if provided
    if (breadcrumb && breadcrumb.items && breadcrumb.items.length > 0) {
      aboutPageSchema.breadcrumb = {
        "@id": absoluteUrl + "#breadcrumb"
      };
    }

    // Add mainEntity reference to Organization
    // Note: Organization schema is injected separately, this references it
    aboutPageSchema.mainEntity = {
      "@id": env.baseOrigin + "/#organization"
    };

    // Create and inject JSON-LD script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-about-page-schema', 'true');
    script.textContent = JSON.stringify(aboutPageSchema, null, 0);
    document.head.appendChild(script);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const scriptToRemove = document.querySelector('script[data-about-page-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, url, breadcrumb, image]); // Re-run if any prop changes

  // Return null since we're using direct DOM manipulation
  return null;
}
